import { useState, useRef, useEffect } from "react";
import { BasketItem, setBasketItemExercise } from "../../../models/BasketItem";
import { Assort } from "../../../models/Assort";
import useFacility from "../../../../utils/hooks/useFacility";
import getId from "../../../../utils/getId";
import { TicketAccess } from "../../../models/TicketAccess";
import { format, isBefore } from "date-fns";

export default function usePreBasket() {
  const [preBasket, setPreBasket] = useState<BasketItem[]>([]);
  const facilityIds = useFacility();
  const isMountedRef = useRef(true);

  // Cleanup function - oznacz komponent jako odmontowany
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const isAssortInBasket = (assort: Assort) => preBasket.some((item) => item.assortIds === getId(assort));

  const findBasketItem = (assort: Assort) => preBasket.find((item) => item.assortIds === getId(assort));

  const checkQnt = (assort: Assort) => {
    const item = findBasketItem(assort);
    return item ? item.qnt : 0;
  };

  const validateRemove = (assort: Assort) => checkQnt(assort) > 1;

  const setBasketItem = (assort: Assort): BasketItem => {
    return {
      assortIds: getId(assort),
      qnt: checkQnt(assort) >= assort.minSale ? 1 : assort.minSale,
      facilityIds,
      price: assort.listPrice,
      validFrom:
        !assort.validFromDate || isBefore(new Date(assort.validFromDate), new Date())
          ? format(new Date(), "yyyy-MM-dd")
          : format(new Date(assort.validFromDate), "yyyy-MM-dd"),
    } as BasketItem;
  };

  const getBasketItemExerciseByAssortMinSale = (assort: Assort, ticketAccess: TicketAccess) => {
    if (checkQnt(assort) >= assort.minSale) {
      return setBasketItemExercise(facilityIds, getId(assort), assort.price, [ticketAccess], null, 1);
    } else {
      return setBasketItemExercise(facilityIds, getId(assort), assort.price, [ticketAccess], null, assort.minSale);
    }
  };

  const handleAddToBasketWithoutTicketAccess = (assort: Assort) => {
    if (!isMountedRef.current) return;

    if (isAssortInBasket(assort)) {
      setPreBasket(preBasket.map((item) => (item.assortIds === getId(assort) ? { ...item, qnt: item.qnt + 1 } : item)));
    } else {
      setPreBasket([setBasketItem(assort), ...preBasket]);
    }
  };

  const handleAddToBasketWithTicketAccess = (assort: Assort, ticketAccess: TicketAccess) => {
    if (!isMountedRef.current) return;

    const assortId = getId(assort);
    const itemIndex = preBasket.findIndex(
      (item) =>
        item.assortIds === assortId &&
        item.ticketAccess.some(
          (t) => t.exerciseIds === ticketAccess.exerciseIds && t.startDate === ticketAccess.startDate
        )
    );

    if (itemIndex > -1) {
      setPreBasket((prevBasket) =>
        prevBasket.map((item, index) => (index === itemIndex ? { ...item, qnt: item.qnt + 1 } : item))
      );
    } else {
      setPreBasket([getBasketItemExerciseByAssortMinSale(assort, ticketAccess), ...preBasket]);
    }
  };

  const addItemToBasket = (assort: Assort, ticketAccess?: TicketAccess) => {
    // getExerciseByPassFlexi(assort.objectId);
    if (ticketAccess && ticketAccess?.exerciseIds) {
      handleAddToBasketWithTicketAccess(assort, ticketAccess);
    } else {
      handleAddToBasketWithoutTicketAccess(assort);
    }
  };

  const handleRemoveItemWithoutTicketAccess = (assort: Assort) => {
    if (!isMountedRef.current) return;

    if (validateRemove(assort)) {
      if (checkQnt(assort) === assort.minSale) {
        setPreBasket(preBasket.filter((item) => item.assortIds !== getId(assort)));
      } else {
        setPreBasket(
          preBasket.map((item) => (item.assortIds === getId(assort) ? { ...item, qnt: item.qnt - 1 } : item))
        );
      }
    } else {
      setPreBasket(preBasket.filter((item) => item.assortIds !== getId(assort)));
    }
  };

  const handleRemoveItemWithWithTicketAccess = (assort: Assort, ticketAccess: TicketAccess) => {
    if (!isMountedRef.current) return;

    const assortId = getId(assort);
    const isTargetItem = (item: BasketItem) =>
      item.assortIds === assortId &&
      item.ticketAccess.some(
        (ticketAccessItem) =>
          ticketAccessItem.exerciseIds === ticketAccess.exerciseIds &&
          ticketAccessItem.startDate === ticketAccess.startDate
      );

    const updatedPreBasket = preBasket.reduce<BasketItem[]>((acc, item) => {
      if (isTargetItem(item)) {
        // Sprawdzamy ilość i decydujemy, czy zmniejszamy `qnt`, czy usuwamy element
        if (item.qnt > assort.minSale) {
          if (assort.minSale === item.qnt) {
            acc.push({ ...item, qnt: item.qnt - assort.minSale });
          } else {
            acc.push({ ...item, qnt: item.qnt - 1 });
          }
        }
        // Jeśli `qnt` wynosi 1, element nie zostanie dodany do `acc`, więc zostanie usunięty
      } else {
        // Dodajemy pozostałe elementy bez zmian
        acc.push(item);
      }
      return acc;
    }, []);

    setPreBasket(updatedPreBasket);
  };

  const removeItemFromBasket = (assort: Assort, ticketAccess?: TicketAccess) => {
    if (ticketAccess && ticketAccess.exerciseIds) {
      handleRemoveItemWithWithTicketAccess(assort, ticketAccess);
    } else {
      handleRemoveItemWithoutTicketAccess(assort);
    }
  };

  const setItemQuantity = (assort: Assort, newQuantity: number, ticketAccess?: TicketAccess) => {
    if (!isMountedRef.current) return;

    const assortId = getId(assort);

    if (newQuantity <= 0) {
      // Usuń item z koszyka jeśli ilość = 0
      if (ticketAccess?.exerciseIds) {
        setPreBasket(
          preBasket.filter(
            (item) =>
              !(
                item.assortIds === assortId &&
                item.ticketAccess?.some(
                  (t) => t.exerciseIds === ticketAccess.exerciseIds && t.startDate === ticketAccess.startDate
                )
              )
          )
        );
      } else {
        setPreBasket(preBasket.filter((item) => item.assortIds !== assortId));
      }
      return;
    }

    if (ticketAccess?.exerciseIds) {
      // Z ticketAccess
      const itemIndex = preBasket.findIndex(
        (item) =>
          item.assortIds === assortId &&
          item.ticketAccess?.some(
            (t) => t.exerciseIds === ticketAccess.exerciseIds && t.startDate === ticketAccess.startDate
          )
      );

      if (itemIndex > -1) {
        // Aktualizuj istniejący item
        setPreBasket((prevBasket) =>
          prevBasket.map((item, index) => (index === itemIndex ? { ...item, qnt: newQuantity } : item))
        );
      } else {
        // Stwórz nowy item
        const newItem = getBasketItemExerciseByAssortMinSale(assort, ticketAccess);
        newItem.qnt = newQuantity;
        setPreBasket([newItem, ...preBasket]);
      }
    } else {
      // Bez ticketAccess
      if (isAssortInBasket(assort)) {
        // Aktualizuj istniejący item
        setPreBasket(preBasket.map((item) => (item.assortIds === assortId ? { ...item, qnt: newQuantity } : item)));
      } else {
        // Stwórz nowy item
        const newItem = setBasketItem(assort);
        newItem.qnt = newQuantity;
        setPreBasket([newItem, ...preBasket]);
      }
    }
  };

  const clearBasket = () => {
    if (!isMountedRef.current) return;
    setPreBasket([]);
  };

  return {
    preBasket,
    addItemToBasket,
    removeItemFromBasket,
    setItemQuantity,
    clearBasket,
  };
}
